'use client'

import type { ErrorMessageProps } from '@pearlconvert/ui/components/error-message/error-message.types'
import type { FieldErrors } from 'react-hook-form'

import classNames from 'classnames'
import { useTranslations } from 'next-intl'
import React from 'react'

import { useErrorMessage } from '@pearlconvert/ui/components/error-message/batons/use-error-message'

import '@pearlconvert/ui/components/error-message/styles/error-message.scss'

const ErrorMessage = (props: ErrorMessageProps) => {
  const { errors, children, className, errorMessageHeader } = props
  const t = useTranslations('ui')

  const { root: _root, ...fieldErrors } = errors ?? {}

  const flattenErrors = (
    errors: FieldErrors | undefined,
    messages: string[] = []
  ): string[] => {
    if (!errors) {
      return messages
    }

    Object.values(errors).forEach((error) => {
      if (
        error &&
        typeof error === 'object' &&
        error.message &&
        typeof error.message === 'string'
      ) {
        messages.push(error.message)
      } else if (typeof error === 'object') {
        flattenErrors(error as FieldErrors, messages)
      }
    })

    return messages
  }

  const errorMessageElements = flattenErrors(fieldErrors)

  useErrorMessage(errorMessageElements, children)

  if (!errorMessageElements.length) {
    if (!children) {
      return
    }
    return (
      <div
        className={classNames(
          'error-message',
          'error-message--simple',
          className
        )}
        aria-live="assertive"
        role="alert"
      >
        {children}
      </div>
    )
  }

  return (
    <div
      className={classNames('error-message', className)}
      role="alert"
      aria-live="assertive"
    >
      <p className="error-message__header">
        {errorMessageHeader ?? t('error-message.not-right')}
      </p>
      <ul className="error-message__list">
        {errorMessageElements.map((message, index) => (
          <li className="error-message__item" key={`error-message-${index}`}>
            {message}
          </li>
        ))}
      </ul>
      {children}
    </div>
  )
}

export default ErrorMessage
